<template>
  <div class="main" :class="{ 'chat-open': match_id }">
    <transition :name="chatTransitionName">
      <!-- OVERVIEW CONTAINER -->
      <div class="chat-overview" v-if="!match_id" key="overview">
        <GroupSelector 
          v-model="selectedPool" 
          :pools="pools" 
          @change="poolChanged()"
        />
        <h1 class="page-title">Chats</h1>
        
        <div v-if="!loading && matches && matches.length === 0" class="center-msg">
          <h3>Du hast noch keine Matches.</h3>
        </div>
        <div v-if="!loading && (!this.pools || this.pools.length === 0)" class="center-msg">
          <h3>Du bist noch kein Mitglied in einer Gruppe!</h3>
          <p>Tritt auf der Startseite zuerst einer Gruppe bei.</p>
        </div>
        
        <div class="chat-list">
          <router-link :to="'/chat?c=' + match.match_id" class="chat" v-for="match in matches" :key="match.match_id">
            <div class="profile-info">
              <h2>{{match.name}}</h2>
              <p class="chat-last-message" v-if="!match.is_typing"><b>{{calcDurationToNow(match.last_timestamp)}}</b> {{match.last_message}}<i v-if="!match.last_message">Noch keine Nachrichten.</i></p>
              <p class="chat-last-message" v-if="!!match.is_typing"><i>schreibt...</i></p>
            </div>
            <button class="open-chat">
              <p v-if="match.unseen && match.unseen > 0" class="unseen-messages">{{ match.unseen }}</p>
              <img src="@/assets/images/back.svg">
            </button>
          </router-link>
          <div v-if="!push_activated">
            <button class="permission-button" @click="onRequestPermissionClick()">Push-Benachrichtigungen aktivieren</button>
          </div>
        </div>
      </div>

      <!-- CHAT DETAIL WRAPPER -->
      <div class="chat-wrapper" v-else-if="match_id" key="detail">
        <div class="chat-header">
          <button @click="$router.back()" class="back-btn">
            <img src="@/assets/images/back.svg" alt="Zurück">
          </button>
          <button class="chat-title" @click="setProfilePreviewVisibility(true)" :disabled="!chat">
            <h2>{{ chat ? chat.name : (activeMatch ? activeMatch.name : 'Laden...') }}</h2>
            <span v-if="partnerTyping" class="status-subtitle typing">schreibt...</span>
            <span v-else class="status-subtitle">Profil anzeigen</span>
          </button>
          <button class="report-btn" @click="reportingProfile = true" :disabled="!chat && !activeMatch">
            <img src="@/assets/images/report.svg" alt="Melden">
          </button>
        </div>

        <div class="message-wrapper" id="message-wrapper">
          <LoadingSpinner v-if="chatLoading" message="Nachrichten werden geladen..." class="center-msg" />
          
          <template v-else-if="chat">
            <div v-if="message" class="system-message">
              <span>{{ message }}</span>
            </div>
            <div 
              class="message" 
              v-for="(msg, i) in chat.messages" 
              :key="msg.message_id" 
              :class="{from_me: msg.author_id === user_id, from_partner: msg.author_id !== user_id}" 
              :id="i === chat.messages.length - 1 ? 'last_message' : null"
            >
              <div class="msg-content-wrapper">
                <p class="content">{{msg.content}}</p>
                <p class="timestamp">
                  <span v-if="msg.seen && i === chat.messages.length - 1 && msg.author_id === user_id && this.user && this.user.messages_seen_on && chat.msg_seen_on" class="seen-status">gelesen • </span>
                  {{msg.timestamp}}
                </p>
              </div>
            </div>
          </template>
        </div>

        <div class="input-wrapper" :class="{ sending: sendingMessage }">
          <div v-if="partnerTyping" id="partner_typing_indicator">
            <span class="typing-dot"></span>
            <span class="typing-dot"></span>
            <span class="typing-dot"></span>
            <i>{{ chat ? chat.name : (activeMatch ? activeMatch.name : '') }} schreibt...</i>
          </div>
          <div class="input-wrapper-input">
            <textarea 
              v-model="newMessage" 
              :disabled="sendingMessage || chatLoading" 
              :placeholder="sendingMessage ? 'Wird gesendet...' : 'Nachricht schreiben...'"
              @keydown.enter.prevent="sendMessageOnEnter"
              rows="1"
              ref="textarea"
              @input="adjustTextareaHeight"
            />
            <button class="send-btn" :disabled="!newMessage || newMessage.trim() === '' || sendingMessage || chatLoading" @click="sendMessage()">
              <img src="@/assets/images/send.svg" alt="Senden">
            </button>
          </div>
        </div>
      </div>
    </transition>

    <LoadingSpinner  v-if="loading" message="Chats werden geladen..." class="center-msg"/>
    
    <div class="report-modal-wrapper darken-page" v-if="reportingProfile && chat">
      <div class="report-modal-content" v-if="!reportSuccessful">
        <div class="report-header">
          <h2>Chat Melden</h2>
          <button class="cancel-report" @click="reportingProfile = false">
            <img src="@/assets/images/cross.svg" alt="Schließen">
          </button>
        </div>
        
        <p class="report-description">
          Du bist dabei, den Chatverlauf mit <b>{{ chat.name }}</b> zu melden.
          <br><br>
          Bitte beschreibe, aus welchem Grund du den Chat meldest. Wir prüfen das Anliegen schnellstmöglich.
        </p>
        
        <div class="report-input-wrapper">
          <textarea 
            v-model="reportingReason" 
            placeholder="Grund für die Meldung (mindestens 6 Zeichen)..."
            rows="4"
          />
          <p v-if="reportingError" class="error-text">{{ reportingError }}</p>
        </div>
        
        <div class="report-actions">
          <button 
            class="submit-report-btn" 
            @click="reportProfile()" 
            :disabled="reportSending || !reportingReason || reportingReason.trim().length < 6"
          >
            <span v-if="reportSending">Wird gesendet...</span>
            <span v-else>Profil melden</span>
          </button>
        </div>
      </div>
      
      <div class="report-modal-content success-state" v-if="reportSuccessful">
        <div class="success-icon-wrapper">
          <span class="success-checkmark">✓</span>
        </div>
        <h2>Erfolgreich gemeldet!</h2>
        <p>
          Vielen Dank für deine Mithilfe! Wir haben dir eine Bestätigungsmail gesendet.
          <br><br>
          Der Chat wird nun ausgeblendet.
        </p>
        <button class="finish-report-btn" @click="finishReport()">
          Abschließen
        </button>
      </div>
    </div>

    <ProfilePreview v-if="profilePreviewVisible" :profile_id="chat ? chat.profile_id : null" @close="setProfilePreviewVisibility(false)"></ProfilePreview>
  </div>
</template>

<script>
import AuthService from '@/services/AuthService.js';
import useWebsocket from "@/services/wsService";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import moment from 'moment';
import registerPushWorker from '@/registerServiceWorker';
import ProfilePreview from "@/components/ProfilePreview.vue";
import GroupSelector from "@/components/GroupSelector.vue";

export default {
  "name": "PlaceholderView",
  components: {
    LoadingSpinner,
    ProfilePreview,
    GroupSelector
  },
  data() {
    return {
      user: null,
      isAdmin: false,
      selectedPool: null,
      pools: null,
      matches: null,
      loading: false,
      message: null,
      chat: null,
      newMessage: null,
      typingStatus: false,
      partnerTyping: false,
      sendingMessage: false,
      push_activated: true,
      profilePreviewVisible: false,
      wsService: useWebsocket(),
      reportingProfile: false,
      reportingReason: null,
      reportingError: null,
      reportSuccessful: false,
      reportSending: false,
      chatTransitionName: 'chat-slide-left',
      chatLoading: false,
    }
  },
  computed: {
    match_id() {
      return this.$route.query.c
    },
    user_id() {
      return this.$store.getters.getUser.id
    },
    activeMatch() {
      if (!this.matches || !this.match_id) return null;
      return this.matches.find(m => m.match_id == this.match_id) || null;
    }
  },
  watch:{
    $route(to, from) {
      const toChat = to?.query?.c;
      const fromChat = from?.query?.c;
      if (toChat && !fromChat) {
        this.chatTransitionName = 'chat-slide-left';
      } else if (!toChat && fromChat) {
        this.chatTransitionName = 'chat-slide-right';
      }
      this.setup();
    },
    newMessage() {
      this.handleTypingStatus();
      this.$nextTick(() => {
        this.adjustTextareaHeight();
      });
    }
  },
  methods: {
    logout() {
      this.$store.dispatch('logout');
      this.$router.push('/login');
    },
    async reportProfile() {
      this.reportingError = null
      if(!this.reportingReason || this.reportingReason.length < 6) {
        this.reportingError = "Bitte gib einen Grund an."
        return -1
      }

      this.reportSending = true
      console.log(this.currentProfile)

      try {
        let result = await AuthService.reportUser({
          reported_user: this.chat.partner_id,
          reported_profile: null,
          reported_match: this.$route.query.c,
          reported_match_name: this.chat.name,
          report_message: this.reportingReason
        })

        if(!result.success) {
          this.reportingError = "Etwas ist schiefgelaufen."
        } else {
          this.reportSuccessful = true
        }
      } catch(e) {
        console.error(e)
        this.reportingError = e
      }

      this.reportSending = false
    },
    finishReport() {
      this.reportingProfile = false;
      this.reportingReason = null;
      this.reportSuccessful = false;
      this.$router.replace({ path: "/chat" });
    },
    poolChanged() {
      this.matches = null
      this.getUserMatches()
      try {
        AuthService.updateSelectedPool({selected_pool: this.selectedPool.id})
        this.$store.dispatch('updateLastSelectedPool', {pool_id: this.selectedPool.id})
      } catch(e) {
        console.error(e)
      }
    },
    sumUnread(arr) {
      return arr.map(a => a.pool_id === this.selectedPool.id ? 0 : a.unreadMessages).reduce((partialSum, a) => partialSum + a, 0);
    },
    setProfilePreviewVisibility(bool){
      this.profilePreviewVisible = bool
    },
    async onRequestPermissionClick() {
      const permission = Notification.permission;

      if (permission === 'default') {
        // ✅ Diese Zeile MUSS direkt im Click-Handler passieren!
        const result = await Notification.requestPermission();
        if (result !== 'granted') {
          console.warn('❌ Berechtigung nicht erteilt.');
          return;
        }
      } else if (permission !== 'granted') {
        console.warn('❌ Berechtigung bereits verweigert.');
        return;
      }

      // Jetzt sicher: Berechtigung ist erteilt
      await registerPushWorker();
    },
    async requestPushPermission(){
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        console.log("granted")
        if ('serviceWorker' in navigator) {
          console.log("re-registering...")
          await registerPushWorker();
        }
      } else {
        console.log("denied")
      }
    },
    async getUserMatches(){
      if (!this.matches) {
        this.loading = true
      }
      if(this.selectedPool?.pool_id) {
        let msg = await AuthService.getUserMatches({pool_id: this.selectedPool.pool_id});
        if(msg.success) {
          this.matches = msg.msg
        }
      }

      this.loading = false
    },
    async getUserJoinedPools() {
      if (!this.pools) {
        this.loading = true
      }
      try {
        this.msg = await AuthService.getUserJoinedPools()
        if (this.msg.success && this.msg.msg.length > 0) {
          this.pools = this.msg.msg;
          this.selectedPool = this.pools.find(res => res.id === this.user.last_selected_pool) || this.pools[0];
        }
      } catch (e) {
        console.error(e)
        this.errorMsg = e.response.data.msg
      }
      this.loading = false
    },
    async loadChat() {
      if(this.match_id) {
        this.chatLoading = true;
        let msg = await AuthService.getChat({match_id: this.match_id, pool_id: this.selectedPool.pool_id});
        if(msg.success) {
          this.chat = {
            name: msg.match.name,
            partner_id: msg.match.id,
            profile_id: msg.match.profile_id,
            msg_seen_on: msg.match.msg_seen_on,
            messages: msg.msg.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)).map(m => { return {...m, timestamp: moment(m.timestamp).format("HH:mm")} }),
          }

          this.message = (msg.msg.length === 0 ? "Noch keine Nachrichten." : null)

          if(this.chat.messages.filter(m => {return m.seen === 0 && m.author_id !== this.user_id}).length > 0) {
            // at least one previously unread msg was fetched. send ws event so other client updates 'seen' status
            this.wsService.sendWSMessage(this.chat.partner_id)
          }

          this.$nextTick(() => { //scroll to bottom after the message wrapper is rendered
            let chatDiv = document.getElementById("message-wrapper")
            if (chatDiv) chatDiv.scrollTop = chatDiv.scrollHeight;
          });
        }
        this.chatLoading = false;
      } else {
        this.chat = null
        await this.getUserMatches()
      }
    },
    adjustTextareaHeight() {
      const textarea = this.$refs.textarea;
      if (!textarea) return;
      textarea.style.height = '32px';
      const scrollHeight = textarea.scrollHeight;
      if (scrollHeight > 80) {
        textarea.style.height = '80px';
        textarea.style.overflowY = 'auto';
      } else {
        textarea.style.height = Math.max(32, scrollHeight) + 'px';
        textarea.style.overflowY = 'hidden';
      }
    },
    sendMessageOnEnter(event) {
      if (event.shiftKey) return;
      this.sendMessage();
    },
    async sendMessage() {
      if (!this.newMessage || !this.newMessage.trim()) return;
      this.sendingMessage = true
      let msg = {
        message_id: Date.now(),
        match_id: this.match_id,
        author_id: this.$store.getters.getUser.id,
        timestamp: moment().format("HH:mm"),
        content: this.newMessage.trim(),
        status: "sending"
      }

      this.chat.messages.push(msg)

      try {
        let res = await AuthService.sendChat(msg)
        if(res.success) {
          this.newMessage = null
          this.message = null;
          this.wsService.sendWSMessage(this.chat.partner_id)
        }
      } catch(e) {
        console.error(e)
      }

      this.sendingMessage = false
      this.$nextTick(() => { //scroll to bottom after the message wrapper is rendered
        let chatDiv = document.getElementById("message-wrapper")
        if (chatDiv) chatDiv.scrollTop = chatDiv.scrollHeight;
      });
    },
    calcDurationToNow(date){
      return date ? (moment(date).isSame(moment(), 'day') ? moment(date).format("HH:mm") : (moment().diff(moment(date), 'hours') > 24 ? moment().diff(moment(date), 'days') + " Tage" : moment().diff(moment(date), 'hours') + " Stunden")) : ""
    },
    handleTypingStatus(){
      let typingStatus = this.newMessage && this.newMessage.length > 0
      if(typingStatus !== this.typingStatus){
        this.typingStatus = typingStatus
        if(typingStatus){
          this.wsService.startedTyping(this.chat.partner_id)
        } else {
          this.wsService.stoppedTyping(this.chat.partner_id)
        }
      }
    },
    handleNewMessage() {
      this.loadChat()
    },
    handlePartnerTyping(data) {
      this.partnerTyping = data.status === 'started';
      const matchIndex = this.matches.findIndex(m => m.user_id == data.sender_id);
      if (matchIndex !== -1) {
        this.matches[matchIndex].is_typing = (data.status === 'started');
      }
    },
    async setup () {
      this.chat = null
      await this.getUserJoinedPools();
      await this.getUserMatches()

      if(this.match_id) await this.loadChat()
    }
  },
  async beforeCreate() {
    if (!this.$store.getters.isLoggedIn) {
      this.$router.push('/login');
    }
  },
  async mounted() {
    try {
      this.msg = await AuthService.getUser()
      this.isAdmin = !!this.msg.msg[0].isAdmin;
      this.user = this.msg.msg[0]
    } catch(e){
      console.error(e)
    }

    AuthService.getPushActivationStatus().then(result => {
      if(result.success){
        this.push_activated = result.msg
      }
    })

    await this.setup()

    this.wsService.on('new_message', this.handleNewMessage);
    this.wsService.on('partner_typing', this.handlePartnerTyping);
    this.wsService.on('ws_message', this.handleGeneralMessage);
    
    this.wsService.startConnection(() => {
      console.log('WebSocket connected');
      this.wsService.joinSession(this.user_id);
    });
  },
  beforeUnmount() {
    if (this.wsService) {
      this.wsService.off('new_message', this.handleNewMessage);
      this.wsService.off('partner_typing', this.handlePartnerTyping);
      this.wsService.off('ws_message', this.handleGeneralMessage);
      this.wsService.closeSession();
    }
  },
}
</script>
<style scoped>
.main {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  background: transparent;
  position: relative;
  overflow: hidden;
  width: 100%;
}

/* Header / Pool Selection */
.select-container {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: var(--glass-bg);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border-color);
  width: 100%;
  box-sizing: border-box;
  position: sticky;
  top: 0;
  z-index: 10;
}

.page-title {
  color: var(--text-on-gradient);
  margin: 20px 0;
}

.selected-pool {
  font-size: 1.1rem;
  font-weight: 700;
  font-family: var(--font);
  background: none;
  border: none;
  color: var(--text-main);
  outline: none;
  cursor: pointer;
}

.unread-messages-cnt {
  background: var(--primary-color);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 800;
}

/* Chat List Items */
.chat-list {
  flex: 1;
  width: 100%;
  max-width: var(--max-width);
  overflow-y: auto;
  padding: 10px 0;
  display: flex;
  gap: 10px;
  flex-direction: column;
  align-items: center;
}

.chat-list a {
  background: var(--card-bg);
  border-radius: var(--radius-l);
  width: 90%;
}

.chat-list a button {
  background: none;
}

.chat-list a button img {
  transform: rotate(180deg);
}

.chat {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  text-decoration: none;
  color: var(--text-main);
  border-bottom: 0.5px solid var(--border-color);
  transition: background 0.2s ease;
}

.chat:active {
  background: #F2F2F7;
}

.chat .profile-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.chat h2 {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
}

.chat-last-message {
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.2;
  margin: 4px 0 4px 0;
}

.unseen-messages {
  background: var(--primary-color);
  color: white;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  box-sizing: border-box;
}

.main.chat-open {
  height: 100%;
  overflow: hidden;
}

/* Individual Chat View */
.chat-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: var(--max-width);
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.chat-header {
  display: grid;
  grid-template-columns: 48px 1fr 48px;
  align-items: center;
  width: 100%;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: var(--shadow-soft);
  z-index: 10;
  box-sizing: border-box;
}

.back-btn, .report-btn {
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
}

.back-btn:active, .report-btn:active {
  background-color: rgba(0, 0, 0, 0.05);
  transform: scale(0.95);
}

.back-btn img {
  width: 20px;
  height: 20px;
  filter: invert(53%) sepia(54%) saturate(464%) hue-rotate(134deg) brightness(91%) contrast(87%);
}

.report-btn img {
  width: 20px;
  height: 20px;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.report-btn:hover img {
  opacity: 1;
}

.chat-title {
  background: none;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 4px 12px;
  border-radius: var(--radius-m);
  transition: background-color 0.2s;
  min-width: 0;
  margin: 0 auto;
  width: 100%;
}

.chat-title:active {
  background-color: rgba(0, 0, 0, 0.05);
}

.chat-title h2 {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-main);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.status-subtitle {
  font-size: 0.75rem;
  color: var(--text-secondary);
  font-weight: 500;
  margin-top: 2px;
}

.status-subtitle.typing {
  color: var(--primary-color);
  font-weight: 600;
  animation: pulse-opacity 1.5s infinite ease-in-out;
}

@keyframes pulse-opacity {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

.message-wrapper {
  flex: 1;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  background: rgba(245, 245, 247, 0.35);
  box-sizing: border-box;
}

.system-message {
  align-self: center;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: var(--text-secondary);
  padding: 6px 14px;
  border-radius: 14px;
  font-size: 0.8rem;
  font-weight: 500;
  margin: 8px 0;
  box-shadow: var(--shadow-soft);
  text-align: center;
}

.message {
  display: flex;
  flex-direction: column;
  max-width: 75%;
}

.message.from_me {
  align-self: flex-end;
  align-items: flex-end;
}

.message.from_partner {
  align-self: flex-start;
  align-items: flex-start;
}

.msg-content-wrapper {
  padding: 10px 16px;
  border-radius: 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  max-width: 100%;
  word-break: break-word;
  box-sizing: border-box;
}

.message.from_me .msg-content-wrapper {
  background: var(--primary-color);
  color: white;
  border-bottom-right-radius: 4px;
}

.message.from_partner .msg-content-wrapper {
  background: white;
  color: var(--text-main);
  border-bottom-left-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.content {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.4;
}

.timestamp {
  font-size: 0.7rem;
  margin-top: 4px;
  opacity: 0.6;
}

.message.from_me .timestamp {
  color: rgba(255, 255, 255, 0.85);
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}

.message.from_partner .timestamp {
  color: var(--text-secondary);
  text-align: left;
}

.seen-status {
  font-style: italic;
  opacity: 0.95;
}

/* Input Area */
.input-wrapper {
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-sizing: border-box;
}

.input-wrapper-input {
  background: #F2F2F7;
  border-radius: 24px;
  padding: 6px 6px 6px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid transparent;
  transition: all 0.2s ease;
}

.input-wrapper-input:focus-within {
  background: white;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(56, 163, 165, 0.15);
}

.input-wrapper-input textarea {
  background: none;
  border: none;
  flex: 1;
  font-family: var(--font);
  font-size: 1rem;
  padding: 6px 0;
  resize: none;
  height: 32px;
  max-height: 80px;
  outline: none;
  color: var(--text-main);
  line-height: 1.25;
  overflow: hidden;
}

.send-btn {
  background: var(--primary-color);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.send-btn:disabled {
  background: var(--text-secondary);
  opacity: 0.3;
  cursor: not-allowed;
}

.send-btn:not(:disabled):active {
  transform: scale(0.9);
}

.send-btn img {
  width: 18px;
  height: 18px;
  filter: brightness(0) invert(1);
  margin-left: 2px;
}

#partner_typing_indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  color: var(--primary-color);
  margin: 0 0 4px 8px;
  font-weight: 500;
}

.typing-dot {
  width: 6px;
  height: 6px;
  background-color: var(--primary-color);
  border-radius: 50%;
  animation: typing-bounce 1.4s infinite ease-in-out both;
}

.typing-dot:nth-child(1) { animation-delay: -0.32s; }
.typing-dot:nth-child(2) { animation-delay: -0.16s; }

@keyframes typing-bounce {
  0%, 80%, 100% { transform: scale(0.2); }
  40% { transform: scale(1); }
}

.permission-button {
  background: var(--glass-bg);
  border: 1px solid var(--border-color);
  padding: 12px 24px;
  border-radius: var(--radius-l);
  font-weight: 600;
  margin: 20px 0;
}

/* Report Modal styling */
.report-modal-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
}

.report-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding-bottom: 12px;
  margin-bottom: 4px;
  width: 100%;
}

.report-header h2 {
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-main);
}

.cancel-report {
  background: rgba(0, 0, 0, 0.05);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
}

.cancel-report:active {
  background-color: rgba(0, 0, 0, 0.1);
  transform: scale(0.9);
}

.cancel-report img {
  width: 14px;
  height: 14px;
  opacity: 0.6;
}

.report-description {
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;
}

.report-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.report-input-wrapper textarea {
  width: 100%;
  background: #F2F2F7;
  border: 1px solid transparent;
  border-radius: var(--radius-m);
  padding: 12px 14px;
  font-family: var(--font);
  font-size: 0.95rem;
  outline: none;
  resize: none;
  box-sizing: border-box;
  transition: all 0.2s ease;
}

.report-input-wrapper textarea:focus {
  background: white;
  border-color: #FF3B30;
  box-shadow: 0 0 0 3px rgba(255, 59, 48, 0.12);
}

.error-text {
  color: #FF3B30;
  font-size: 0.8rem;
  font-weight: 600;
  margin: 0;
}

.submit-report-btn {
  width: 100%;
  background: #FF3B30;
  color: white;
  border: none;
  padding: 14px;
  border-radius: var(--radius-m);
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.submit-report-btn:disabled {
  background: var(--text-secondary);
  opacity: 0.4;
  cursor: not-allowed;
}

.submit-report-btn:not(:disabled):active {
  transform: scale(0.98);
  background: #E02B20;
}

/* Success State styling */
.success-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 24px !important;
}

.success-icon-wrapper {
  width: 60px;
  height: 60px;
  background-color: rgba(52, 199, 89, 0.12);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.success-checkmark {
  font-size: 1.8rem;
  color: #34C759;
  font-weight: bold;
}

.success-state h2 {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-main);
}

.success-state p {
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 12px 0 24px;
}

.finish-report-btn {
  width: 100%;
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 14px;
  border-radius: var(--radius-m);
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.finish-report-btn:active {
  transform: scale(0.98);
}

.report-modal-content {
  background: white;
  border-radius: var(--radius-xl);
  padding: 32px 24px;
  box-shadow: var(--shadow-strong);
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: modalScaleUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-sizing: border-box;
}

@keyframes modalScaleUp {
  from {
    transform: scale(0.9) translateY(20px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}
/* Chat Transition: Slide & Fade */
.chat-slide-left-enter-active,
.chat-slide-left-leave-active,
.chat-slide-right-enter-active,
.chat-slide-right-leave-active {
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Slide Left Enter: new view (detail) comes from right */
.chat-slide-left-enter-from {
  transform: translateX(100%);
  opacity: 0;
}
/* Slide Left Leave: old view (list) goes to left */
.chat-slide-left-leave-to {
  transform: translateX(-30%);
  opacity: 0;
}

/* Slide Right Enter: new view (list) comes from left */
.chat-slide-right-enter-from {
  transform: translateX(-30%);
  opacity: 0;
}
/* Slide Right Leave: old view (detail) goes to right */
.chat-slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.chat-overview {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}
</style>
